<script setup lang="ts">
// THE SEAM (§10) — ports v3 `CustomDashboardDataVisible.vue` (241 ln).
//
// Legacy was an 11-branch `v-if`/`v-else-if` chain over `col.visibleType` whose script block
// hard-imported six other features (`:130-137`), so a dashboard depended on budget, scorecards,
// insights, audience and alerts all resolving — and carried all five in its chunk.
//
// The dispatch here is five cases, not eleven, and it imports no feature but 08:
//   • report kinds (chart/table/matrix/card/double)  → `ReportWidget` → 08's `DataVisible`
//   • `dashboard_chart` / `score` / `list_table`      → 09's own widgets on 08's data layer
//   • everything else                                 → `EmbedWidget` → the registry
import { computed, inject, ref, unref, watch } from 'vue'

import { widgetCapabilities } from '../../models/widget.types'
import type { DashboardWidget } from '../../models/widget.types'
import type { WidgetDataSource, WidgetSheet } from '../../models/widgetExport.types'
import {
  isReportWidgetKind,
  resolveTargetCurrency,
  widgetDatePreset,
  widgetDatePickerHydration,
  widgetDefaultPostData,
} from '../../service/widgetSpec'
import EmbedWidget from '../widgets/EmbedWidget.vue'
import ListTableWidget from '../widgets/ListTableWidget.vue'
import OverviewChartWidget from '../widgets/OverviewChartWidget.vue'
import ReportWidget from '../widgets/ReportWidget.vue'
import ScoreWidget from '../widgets/ScoreWidget.vue'

import { ReportDatePicker } from '@/features/reports'
import { useI18n } from '@/shared/i18n'

const props = withDefaults(
  defineProps<{
    widget: DashboardWidget
    wsId?: number | string | null
    accountWorkspaces?: readonly (number | string)[]
    readOnly?: boolean
    /** Sheet-name prefixes for the export fan-out; the grid supplies them (§8). */
    tabName?: string
    sectionName?: string
  }>(),
  { wsId: null, accountWorkspaces: () => [], readOnly: false, tabName: '', sectionName: '' },
)
const emit = defineEmits<{ 'has-insights-change': [visible: boolean] }>()
const { t } = useI18n()

/**
 * The account-center currency, provided by `AccountCenterPage`. Injected once here rather than in
 * each widget: `service/**` is pure, so the resolved code is threaded down as a prop. 08's
 * `useReportCellFormatter` injects the same key for DISPLAY; this copy is what puts
 * `target_currency` on the REQUEST.
 */
const currencyOverride = inject<unknown>('accountCenterSelectedCurrency', null)
const targetCurrency = computed(() => resolveTargetCurrency(unref(currencyOverride)) ?? null)

const title = computed(() => props.widget.wire.name ?? '')
const isOverview = computed(() => props.widget.kind === 'dashboard_chart')
const isScore = computed(() => props.widget.kind === 'score')
const isListTable = computed(() => props.widget.kind === 'list_table')
/**
 * Title + date picker belong to the host only for the kinds 09 renders itself. A registry embed
 * receives `title` as a prop and draws its own heading (legacy passed `:title` to all six), so
 * adding one here would double it.
 */
const ownsChrome = computed(
  () =>
    isReportWidgetKind(props.widget.kind) || isOverview.value || isScore.value || isListTable.value,
)
const showDatePicker = computed(
  () =>
    ownsChrome.value &&
    props.widget.kind !== null &&
    widgetCapabilities(props.widget.kind)?.datePicker === true &&
    props.widget.wire.hideDatePicker !== true,
)

/**
 * The per-widget date range. A dashboard has no page-level date picker (§14) — each block owns one,
 * and changing it refetches only that block. The seed exists because 08's `ReportDatePicker`
 * restores a PRESET but not an explicit range, and it emits `change` immediately on mount: without
 * skipping that first emit, a widget saved on a custom range would silently reopen on the default.
 * The picker re-emits whenever its own defaults change — exactly when this watch re-arms the skip.
 */
const persistedRange = computed(() => widgetDefaultPostData(props.widget.wire))
const datePickerHydration = computed(() => widgetDatePickerHydration(props.widget.wire))
const defaultPostData = ref('')
let skipNextPickerEmit = false

watch(
  persistedRange,
  (value) => {
    defaultPostData.value = value
    skipNextPickerEmit = value !== ''
  },
  { immediate: true },
)

function onDateChange(next: string): void {
  if (skipNextPickerEmit) {
    skipNextPickerEmit = false
    return
  }
  defaultPostData.value = next
}

/** Whichever data-owning widget is mounted; every 09-owned renderer exposes the same shape. */
const renderer = ref<WidgetDataSource | null>(null)

/**
 * The export fan-out's per-widget leaf (§8). It delegates to whichever renderer is mounted and
 * returns `[]` when that renderer owns no rows — today that is every `ReportWidget`, because 08's
 * `DataVisible` exposes nothing (the ask is filed against 08; see the PR).
 */
async function downloadData(): Promise<WidgetSheet[]> {
  const sheets = (await renderer.value?.downloadData()) ?? []
  return sheets.map((sheet) => ({
    ...sheet,
    name: [props.tabName, props.sectionName, sheet.name].filter(Boolean).join('-'),
  }))
}

defineExpose({ downloadData })
</script>

<template>
  <div class="dashboard-widget-host flex min-h-0 flex-1 flex-col">
    <header
      v-if="ownsChrome"
      class="dashboard-widget-host__header flex items-center justify-between gap-sm px-sm py-xs"
    >
      <h3
        class="dashboard-widget-host__title min-w-0 truncate overflow-hidden text-[0.9375rem] font-semibold text-primary"
      >
        {{ title || t('dashboards.widget.untitled') }}
      </h3>
      <!-- The picker fills its container; sized to its content here, it leaves the title its room. -->
      <div v-if="showDatePicker" class="dashboard-widget-host__date ml-auto w-max shrink-0">
        <ReportDatePicker
          :id="`dashboard-widget:${widget.wire.id ?? widget.wire.visibleType ?? 'unknown'}`"
          :default-preset="widgetDatePreset(widget.wire)"
          :compare-default="widget.wire.compareEnabled !== false"
          :hydration="datePickerHydration"
          @change="onDateChange"
        />
      </div>
    </header>

    <ReportWidget
      v-if="isReportWidgetKind(widget.kind)"
      :widget="widget"
      :title="title"
      :ws-id="wsId"
      :account-workspaces="accountWorkspaces"
      :target-currency="targetCurrency"
      :default-post-data="defaultPostData"
      :read-only="readOnly"
    />
    <OverviewChartWidget
      v-else-if="isOverview"
      ref="renderer"
      :widget="widget"
      :title="title"
      :ws-id="wsId"
      :account-workspaces="accountWorkspaces"
      :target-currency="targetCurrency"
      :default-post-data="defaultPostData"
    />
    <ScoreWidget
      v-else-if="isScore"
      ref="renderer"
      :widget="widget"
      :title="title"
      :ws-id="wsId"
      :account-workspaces="accountWorkspaces"
      :target-currency="targetCurrency"
      :default-post-data="defaultPostData"
    />
    <ListTableWidget
      v-else-if="isListTable"
      ref="renderer"
      :widget="widget"
      :title="title"
      :ws-id="wsId"
      :account-workspaces="accountWorkspaces"
      :target-currency="targetCurrency"
      :default-post-data="defaultPostData"
    />
    <EmbedWidget
      v-else
      :widget="widget"
      :title="title"
      :ws-id="wsId"
      :account-workspaces="accountWorkspaces"
      :read-only="readOnly"
      @has-insights-change="emit('has-insights-change', $event)"
    />
  </div>
</template>
